/* eslint-disable ember/use-ember-get-and-set */
import { action } from '@ember/object';
import { isBlank } from '@ember/utils';
import type Questionnaire from 'garaje/models/questionnaire';
import type QuestionnaireField from 'garaje/models/questionnaire-field';
import type QuestionnaireFieldOption from 'garaje/models/questionnaire-field-option';
import type QuestionnaireSection from 'garaje/models/questionnaire-section';
import { SignInFieldKind } from 'garaje/utils/enums';
import { TrackedMap, TrackedSet } from 'tracked-built-ins';

export default class QuestionnaireValidator {
  #questionnaire: Questionnaire;

  #questionnaireErrors = new TrackedSet<string>();
  #sectionErrors = new TrackedMap<QuestionnaireSection, Set<string>>();
  #questionErrors = new TrackedMap<QuestionnaireField, Set<string>>();
  #questionOptionErrors = new TrackedMap<QuestionnaireFieldOption, Set<string>>();

  constructor(questionnaire: Questionnaire) {
    this.#questionnaire = questionnaire;
  }

  get hasErrors(): boolean {
    if (this.#questionnaireErrors.size > 0) return true;

    for (const errors of this.#sectionErrors.values()) {
      if (errors.size > 0) return true;
    }

    for (const errors of this.#questionErrors.values()) {
      if (errors.size > 0) return true;
    }

    for (const errors of this.#questionOptionErrors.values()) {
      if (errors.size > 0) return true;
    }

    return false;
  }

  @action
  questionnaireErrors(): string[] {
    return [...this.#questionnaireErrors];
  }

  @action
  questionErrors(question: QuestionnaireField): string[] {
    const errors = this.#questionErrors.get(question) ?? [];
    return [...errors];
  }

  @action
  questionOptionErrors(option: QuestionnaireFieldOption): string[] {
    const errors = this.#questionOptionErrors.get(option) ?? [];
    return [...errors];
  }

  @action
  sectionErrors(section: QuestionnaireSection): string[] {
    const errors = this.#sectionErrors.get(section) ?? [];
    return [...errors];
  }

  validate(): boolean {
    this.#reset();

    this.#validateQuestionnaire(this.#questionnaire);
    const sections = this.#questionnaire.questionnaireSections.filter((section) => !section.isDeleted);
    for (const section of sections) {
      this.#validateSection(section);
    }

    return !this.hasErrors;
  }

  #reset(): void {
    this.#questionnaireErrors.clear();
    this.#sectionErrors.clear();
    this.#questionErrors.clear();
    this.#questionOptionErrors.clear();
  }

  #validateQuestionnaire(questionnaire: Questionnaire): void {
    if (isBlank(questionnaire.title)) {
      this.#questionnaireErrors.add('You must set a title for this assessment');
    }

    const sections = this.#questionnaire.questionnaireSections.filter((section) => !section.isDeleted);
    if (sections.length === 0) {
      this.#questionnaireErrors.add('This assessment must have at least one section');
    }
  }

  #validateQuestion(question: QuestionnaireField): void {
    const errors = new Set<string>();
    if (isBlank(question.name)) {
      errors.add('Please enter some text for this question.');
    }

    const options = question.questionnaireFieldOptions.filter((option) => !option.isDeleted);

    if (question.kind === SignInFieldKind.SINGLE_SELECTION) {
      // If this is a single-selection question, one (and only) one option must be marked as the correct answer.
      if (options.filter(({ isCorrect }) => isCorrect).length !== 1) {
        errors.add('This question is missing a correct answer. Please mark the correct answer.');
      }
    } else if (question.kind === SignInFieldKind.MULTIPLE_SELECTION) {
      // If this is a multiple-selection question, at least one option must be marked as a correct answer.
      if (options.filter(({ isCorrect }) => isCorrect).length < 1) {
        errors.add('This question is missing a correct answer. Please mark the correct answer(s).');
      }
    }
    // question must have at least two options
    if (options.length < 2) {
      errors.add('This question must have least two options.');
    }

    this.#questionErrors.set(question, errors);

    for (const option of options) {
      this.#validateQuestionOption(question, option);
    }
  }

  #validateQuestionOption(question: QuestionnaireField, option: QuestionnaireFieldOption): void {
    const errors = new Set<string>();
    const options = question.questionnaireFieldOptions.filter((option) => !option.isDeleted);
    if (isBlank(option.value)) {
      if (options.length <= 2) {
        errors.add('Please complete this option.');
      } else {
        errors.add('Please complete this option or delete it.');
      }
    }

    this.#questionOptionErrors.set(option, errors);
  }

  #validateSection(section: QuestionnaireSection): void {
    const errors = new Set<string>();
    const questions = section.questionnaireFields.filter((question) => !question.isDeleted);
    if (questions.length === 0) {
      errors.add('This section must have at least one question');
    }

    this.#sectionErrors.set(section, errors);

    for (const question of questions) {
      this.#validateQuestion(question);
    }
  }
}
